import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Buildings, SpinnerGap, UserPlus, Warning } from "@phosphor-icons/react";

import { API_BASE as API } from "../lib/apiBase";

export default function InvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", password: "", partner_code: "" });
  const [formError, setFormError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/organizations/invite/${token}`);
        setInvite(data);
        // Vincent iter83 — prefill name + partner_code (pas seulement email)
        setFormData((prev) => ({
          ...prev,
          email: data.email || "",
          name: data.name || "",
          partner_code: data.partner_code || "",
        }));
      } catch (err) {
        setError(err.response?.status === 404 ? "invalid" : "error");
      }
      setLoading(false);
    })();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError("");
    try {
      await axios.post(`${API}/api/auth/register`, {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        org_invite_token: token,
      }, { withCredentials: true });
      navigate("/dashboard");
    } catch (err) {
      setFormError(err.response?.data?.detail || "Erreur lors de l'inscription");
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <SpinnerGap size={40} className="animate-spin text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <Warning size={48} className="mx-auto mb-4 text-red-400" />
          <h1 className="text-2xl font-bold text-white mb-2">Invitation invalide</h1>
          <p className="text-zinc-400 text-sm">Cette invitation n'existe pas ou a expire.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-4" data-testid="invite-page">
      <div className="w-full max-w-md">
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
              <Buildings size={24} className="text-blue-500" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white font-['Outfit']">Rejoindre {invite.org_name}</h2>
              <p className="text-sm text-zinc-400">
                Invite par <span className="text-zinc-200">{invite.invited_by}</span> en tant que <span className="text-blue-400">{invite.role}</span>
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm text-zinc-300 block mb-1">Votre nom</label>
              <input
                type="text" required value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full h-10 px-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                data-testid="invite-name-input"
              />
            </div>
            <div>
              <label className="text-sm text-zinc-300 block mb-1">Email</label>
              <input
                type="email" required value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full h-10 px-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                disabled={!!invite.email}
                data-testid="invite-email-input"
              />
            </div>
            <div>
              <label className="text-sm text-zinc-300 block mb-1">Mot de passe</label>
              <input
                type="password" required value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full h-10 px-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                minLength={8}
                data-testid="invite-password-input"
              />
            </div>
            {invite.partner_code && (
              <div>
                <label className="text-sm text-zinc-300 block mb-1">Code partenaire</label>
                <input
                  type="text" value={formData.partner_code} readOnly
                  className="w-full h-10 px-3 bg-zinc-800/60 border border-zinc-700 rounded-lg text-zinc-300 text-sm"
                  data-testid="invite-partner-code-input"
                />
              </div>
            )}

            {formError && <p className="text-red-400 text-sm">{formError}</p>}

            <button
              type="submit" disabled={submitting}
              className="w-full h-11 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
              data-testid="invite-submit-btn"
            >
              {submitting ? <SpinnerGap size={20} className="animate-spin" /> : <UserPlus size={20} />}
              {submitting ? "Inscription..." : "Rejoindre l'equipe"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
